/**
 * Public enrollment page — real Square-hosted card checkout.
 * Route: /coaching/:slug/enroll
 *
 * REPLACES the legacy manual-payment intake (Zelle/CashApp/etc.). The
 * client sees a single Pay-in-Full button — split payments are handled
 * natively by Square's Afterpay/Clearpay on the hosted checkout page
 * (offered when the buyer is eligible). We mint a real Square checkout
 * via `/api/coaching/checkout/create-session`, and the browser is
 * redirected to Square's hosted card page. On return, the
 * `/enroll/return` sibling route polls status and either drops the
 * client into Module 1 or shows an "email is on its way" fallback.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck, CreditCard, Wallet } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { SharedHeader, SharedFooter } from "./CoachingProgramPublic";

const API = process.env.REACT_APP_BACKEND_URL;

const PALETTE = {
  purple: "#4C1D95",
  purpleSoft: "#7C3AED",
  gold: "#B8924B",
  ink: "#0B0B0F",
  paper: "#FAF8F4",
};

// Locked pricing table — must match backend/coaching/square_catalog.py.
// Rendered by the Pay-in-Full button. Source-of-truth is the backend;
// this mirror exists ONLY to render the button label before the client
// has clicked, so we never wait on a round-trip to show the price.
const PRICING = {
  "the-me-i-see": { full: 997.0 },
  "healing-what-you-cannot-feel": { full: 1497.0 },
  "she-is": { full: 2497.0 },
};

function fmt(n) {
  return `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CoachingIntake() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useDocumentMeta({
    title: program?.title
      ? `Enroll in ${program.title}™ · Dr. Christy Vonta Coaching`
      : "Enrollment · Dr. Christy Vonta Coaching",
    description: program?.title
      ? `Secure card checkout for ${program.title}™ — pay in full or split via Afterpay/Clearpay at checkout.`
      : "Secure card checkout for a Dr. Christy Vonta coaching program.",
  });
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/coaching/programs/public/${slug}`);
        if (!r.ok) throw new Error("not found");
        const data = await r.json();
        setProgram(data.program);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function beginCheckout(planType) {
    setErrMsg("");
    if (!form.email || !form.name) {
      setErrMsg("Enter your name and email first, then choose a payment option.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/coaching/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          program_slug: slug,
          plan_type: planType, // "full" | "installment"
          email: form.email,
          name: form.name,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setErrMsg(
          data?.detail?.message ||
            (typeof data?.detail === "string" ? data.detail : null) ||
            "Checkout could not be created. Try again in a moment."
        );
        setSubmitting(false);
        return;
      }
      if (!data.checkout_url) {
        setErrMsg("Checkout link unavailable. Please try again in a moment.");
        setSubmitting(false);
        return;
      }
      // Hard-redirect the browser to Square's hosted card page. Square
      // handles all card entry (Visa/MC/Amex/Discover) plus native
      // Afterpay/Clearpay when the buyer is eligible; we never see
      // the card number.
      window.location.href = data.checkout_url;
    } catch (e) {
      setErrMsg(e.message || "Network error.");
      setSubmitting(false);
    }
  }

  if (notFound)
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-8"
           style={{ background: PALETTE.paper }}>
        <div>
          <div className="font-serif text-3xl text-slate-900">Program not found</div>
          <div className="text-sm text-slate-600 mt-2">This program is not currently published.</div>
          <Link to="/coaching" className="mt-4 inline-block underline text-sm"
                style={{ color: PALETTE.purple }}>
            ← Back to Coaching
          </Link>
        </div>
      </div>
    );
  if (!program)
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PALETTE.paper }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: PALETTE.purple }} />
      </div>
    );

  const pricing = PRICING[slug];

  return (
    <div className="min-h-screen"
         style={{ background: PALETTE.paper, color: PALETTE.ink, fontFamily: "'Inter', sans-serif" }}
         data-testid="coaching-intake-page">
      <SharedHeader />

      <main className="max-w-2xl mx-auto px-6 py-14">
        <Link to={`/coaching/${slug}`}
              className="text-xs mb-6 inline-block hover:opacity-70"
              style={{ color: PALETTE.purple }}
              data-testid="intake-back-link">
          ← Back to {program.title}
        </Link>

        <div className="text-[11px] uppercase mb-5"
             style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}>
          Enrollment · {program.title}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05]" style={{ color: PALETTE.ink }}>
          Step forward.
        </h1>
        <p className="mt-4 text-[15.5px] leading-relaxed" style={{ color: "#332C3A" }}>
          Secure card checkout via Square. The moment your payment clears,
          your access link arrives by email — and you&apos;ll be dropped straight
          into <strong>Module 1</strong> if you&apos;re returning to this browser.
          Prefer to split it up? Eligible buyers can choose{" "}
          <strong>Afterpay/Clearpay</strong> at checkout for 4 biweekly
          installments.
        </p>

        <div className="mt-8 space-y-4" data-testid="intake-form">
          <Field label="Full name">
            <input required value={form.name} onChange={(e) => up("name", e.target.value)}
                   data-testid="intake-name"
                   className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:border-purple-900"
                   placeholder="Your full name" />
          </Field>
          <Field label="Email">
            <input type="email" required value={form.email} onChange={(e) => up("email", e.target.value)}
                   data-testid="intake-email"
                   className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:border-purple-900"
                   placeholder="you@example.com" />
          </Field>
        </div>

        {pricing && (
          <div className="mt-8 grid grid-cols-1 gap-4" data-testid="intake-plan-buttons">
            <PlanCard
              title="Pay in Full"
              price={fmt(pricing.full)}
              subline="One card charge. Instant access."
              testid="intake-plan-full"
              disabled={submitting}
              onClick={() => beginCheckout("full")}
            />
            <div
              className="rounded-2xl bg-white/60 border border-slate-200 px-4 py-3 flex items-start gap-3"
              data-testid="intake-afterpay-hint"
            >
              <Wallet
                className="w-4 h-4 mt-0.5 shrink-0"
                style={{ color: PALETTE.purple }}
              />
              <div className="text-xs text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">
                  Split payments available at checkout
                </span>{" "}
                via Afterpay/Clearpay — subject to eligibility. Choose it on
                the Square page after you continue.
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500"
             data-testid="intake-card-badges">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Secure checkout by Square · Visa, Mastercard, Amex, Discover, Afterpay/Clearpay</span>
        </div>

        {errMsg && (
          <div className="mt-6 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3"
               data-testid="intake-error">
            {errMsg}
          </div>
        )}

        {submitting && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-600"
               data-testid="intake-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Opening secure checkout…
          </div>
        )}

        <p className="mt-10 text-[11px] text-slate-500 leading-relaxed text-center">
          By continuing, you agree that this enrollment fee is a purchase
          of coaching services. Card processing is handled by Square; we
          never see or store your card number.
        </p>
      </main>
      <SharedFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function PlanCard({ title, price, subline, testid, disabled, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
            className="text-left rounded-2xl border-2 border-slate-200 hover:border-purple-900 transition p-5 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ boxShadow: "0 8px 24px -18px rgba(76,29,149,0.35)" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{title}</div>
        <CreditCard className="w-4 h-4 text-purple-900" />
      </div>
      <div className="font-serif text-2xl text-slate-900">{price}</div>
      <div className="text-xs text-slate-500 mt-2">{subline}</div>
      <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-purple-900">
        Continue to secure checkout <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}
